import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';

const ParentDashboard = () => {
  const { user, isParent } = useAuth();
  const [confirmState, setConfirmState] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkFormData, setLinkFormData] = useState({
    playerEmail: '',
    relationshipType: 'parent',
    isPrimary: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/parent-player/my-players', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      setPlayers(response.data.players);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load your players');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && isParent) {
      fetchPlayers();
    }
  }, [user, isParent, fetchPlayers]);

  const handleLinkFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLinkFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLinkPlayer = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post('/api/parent-player/link', linkFormData, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      toast.success('Player linked successfully!');
      setShowLinkForm(false);
      setLinkFormData({
        playerEmail: '',
        relationshipType: 'parent',
        isPrimary: false
      });
      fetchPlayers(); // Refresh the list
    } catch (error) {
      console.error('Error linking player:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to link player');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlinkPlayer = (relationshipId) => {
    setConfirmState({
      title: 'Unlink Player',
      message: 'Are you sure you want to unlink this player?',
      isDangerous: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/api/parent-player/relationship/${relationshipId}`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          });
          toast.success('Player unlinked successfully!');
          fetchPlayers();
        } catch (error) {
          console.error('Error unlinking player:', error);
          toast.error('Failed to unlink player');
        }
      },
    });
  };

  if (!isParent) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">This dashboard is only available for parents.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">My Players</h2>
            <p className="text-gray-600 mt-1">Manage your children's team memberships and information</p>
          </div>
          <button
            onClick={() => setShowLinkForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Link Player
          </button>
        </div>
      </div>

      {/* Link Player Form */}
      {showLinkForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Link a Player</h3>
          <form onSubmit={handleLinkPlayer} className="space-y-4">
            <div>
              <label htmlFor="playerEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Player's Email Address
              </label>
              <input
                type="email"
                id="playerEmail"
                name="playerEmail"
                value={linkFormData.playerEmail}
                onChange={handleLinkFormChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter player's email address"
              />
              <p className="text-sm text-gray-500 mt-1">
                The player must have an account with this email address.
              </p>
            </div>

            <div>
              <label htmlFor="relationshipType" className="block text-sm font-medium text-gray-700 mb-2">
                Relationship Type
              </label>
              <select
                id="relationshipType"
                name="relationshipType"
                value={linkFormData.relationshipType}
                onChange={handleLinkFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="parent">Parent</option>
                <option value="guardian">Guardian</option>
                <option value="emergency_contact">Emergency Contact</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPrimary"
                name="isPrimary"
                checked={linkFormData.isPrimary}
                onChange={handleLinkFormChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900">
                This is my primary relationship with this player
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowLinkForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !linkFormData.playerEmail}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Linking...' : 'Link Player'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Players List */}
      {players.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No players linked</h3>
            <p className="mt-1 text-sm text-gray-500">
              You haven't linked any players to your account yet.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowLinkForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Link Your First Player
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-6">
          {players.map((item) => (
            <div key={item.relationship.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {item.player.profile_image ? (
                        <img
                          className="h-10 w-10 rounded-full"
                          src={item.player.profile_image}
                          alt={item.player.name}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {item.player.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{item.player.name}</h3>
                      <p className="text-sm text-gray-600">{item.player.email}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.relationship.relationship_type === 'parent' 
                            ? 'bg-blue-100 text-blue-800'
                            : item.relationship.relationship_type === 'guardian'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.relationship.relationship_type.replace('_', ' ')}
                        </span>
                        {item.relationship.is_primary && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Primary
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Player Details */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Position</p>
                      <p className="text-sm font-medium text-gray-900">
                        {item.player.position || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Jersey Number</p>
                      <p className="text-sm font-medium text-gray-900">
                        {item.player.jersey_number || 'Not assigned'}
                      </p>
                    </div>
                  </div>

                  {/* Teams */}
                  {item.teams.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-2">Teams</p>
                      <div className="space-y-2">
                        {item.teams.map((team) => (
                          <div key={team.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{team.name}</p>
                              <p className="text-xs text-gray-600">
                                {team.sport} • {team.ageGroup} • {team.season}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                team.relationship_type === 'player' 
                                  ? 'bg-green-100 text-green-800'
                                  : team.relationship_type === 'coach'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {team.relationship_type}
                              </span>
                              <a
                                href={`/events?teamId=${team.id}`}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                Schedule
                              </a>
                              <a
                                href={`/chat?teamId=${team.id}`}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                Messages
                              </a>
                              <a
                                href={`/roster?teamId=${team.id}`}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                Roster
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Permissions */}
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">Your Permissions</p>
                    <div className="flex flex-wrap gap-2">
                      {item.relationship.can_manage_uniforms && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Manage Uniforms
                        </span>
                      )}
                      {item.relationship.can_view_invoices && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          View Invoices
                        </span>
                      )}
                      {item.relationship.can_manage_schedule && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Manage Schedule
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0 ml-4">
                  <button
                    onClick={() => handleUnlinkPlayer(item.relationship.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Unlink
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default ParentDashboard;
